import React, { useCallback, useEffect, useRef, useState } from 'react';
import Badge from 'src/components/ui/Badge/Badge';
import Divider from 'src/components/ui/Divider/Divider';
import { formatDateAndTime, resolveBadgeTypeForStatus } from 'src/utils/general';
import { useTranslation } from 'react-i18next';
import Textarea from 'src/components/ui/Textarea/Textarea';
import LoadingSkeleton from 'src/components/ui/LoadingSkeleton/LoadingSkeleton';
import Chat from 'src/components/layouts/Chat/Chat';
import ContainerBody from 'src/components/ui/ContainerOverlay/ContainerBody/ContainerBody';
import ConfirmationModal from 'src/components/modals/ConfirmationModal/ConfirmationModal';
import {
  SaleOrder,
  SaleOrderLine,
  SaleOrderReadExt,
  useSaleOrderCancel,
  useSaleOrderConfirm,
  useSaleOrderSetToDraft,
} from 'src/services/saleOrder';
import { Partner } from 'src/services/partner';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import ProductTable from './ProductTable/ProductTable';
import AdressForm from './AdressForm/AdressForm';
import { validateOutboundEntry } from './validateOutboundEntry';
import { SelectProductsFromInventory } from '../SelectProducts/SelectProducts';
import Head from './Head/Head';
import PrimaryInfo from './PrimaryInfo/PrimaryInfo';
import ShippingLines from './ShippingLines/ShippingLines';
import Attachments from './Attachments/Attachments';
import FbaLabel from './FbaLabel/FbaLabel';
import { useFbaSettingsQuery } from '../../../services/fbaSettings';
import { OverlaySectionHeadline } from '../OverlaySectionHeadline/OverlaySectionHeadline';
import { useOutboundOrder } from '../../../hooks/useOutboundOrder';
import { useNavigationItems } from '../../../hooks/useNavigationItems';
import { useReturnsQuery } from '../../../services/returns';
import styles from '../../../pages/ReturnOverlay/ReturnOverlay.module.scss';
import Button, { ButtonToneProps } from '../../ui/Button/Button';
import Checkbox from '../../ui/Checkbox/Checkbox';
import { useCaseMutations } from '../../../services/case';
import LinkedCaseTable from './LinkedCasesTable/LinkedCasesTable';
import { useProfile } from '../../../utils/ProfileService';
import { useOverlayNavGuard } from '../../../providers/OverlayNavProvider';
import { useOverlay } from '../../../providers/OverlayProvider';

const Status = {
  draft: 'draft',
  open: 'open',
  cancel: 'cancel',
  in_progress: 'in_progress',
  under_investigation: 'under_investigation',
  not_available: 'not_available',
} as const;

// Claim creation (Klärung / Schadensmeldung) is only offered for SHIPPED
// outbounds. TODO(deploy): keep this `false`. Flip to `true` only for local
// testing when the dev DB has no shipped outbounds to click through.
const CLAIM_ENTRY_TESTING_SHOW_ALL = false;

export const OutboundOverlay: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { onClose } = useOverlay();
  const [isProductClickModalOpen, setProductClickModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null);
  const [isCaseClickModalOpen, setCaseClickModalOpen] = useState(false);
  const [selectedCaseId, setSelectedCaseId] = useState<number | null>(null);
  const closeAfterConfirmRef = useRef(false);
  const {
    saleOrder,
    saleOrderItem,
    loading,
    edit,
    showSelectProducts,
    isCancelConfirmationOpen,
    handleAction,
    handleOrderLineDelete,
    handleConfirmClose,
    updateSaleOrderItem,
    handleProductsSelected,
    setEdit,
    setShowSelectProducts,
    setIsCancelConfirmationOpen,
    hasChanges,
  } = useOutboundOrder();

  const defaultProductSelection = React.useMemo(() => {
    const lines = (saleOrderItem.order_lines ?? saleOrder?.order_lines ?? []) as any[];
    return lines
      .filter(line => line.product)
      .map(line => ({
        id: line.product.id,
        product_id: line.product.id,
        name: line.product.name,
        barcode: line.product.barcode,
        article_number: line.product.article_number,
        quantity: line.quantity,
        tracking: line.product.tracking,
      }));
  }, [saleOrderItem.order_lines, saleOrder?.order_lines]);

  const pendingNavRef = useRef<(() => void) | null>(null);

  useOverlayNavGuard(
    edit
      ? (navigateFn: () => void) => {
          if (hasChanges) {
            pendingNavRef.current = navigateFn;
            setIsCancelConfirmationOpen(true);
          } else {
            setEdit(false);
            navigateFn();
          }
        }
      : null,
  );

  const overlayRef = useRef<HTMLDivElement>(null);

  const handleOutsideClose = useCallback(() => {
    if (hasChanges) {
      closeAfterConfirmRef.current = true;
      setIsCancelConfirmationOpen(true);
    } else {
      setEdit(false);
      onClose();
    }
  }, [hasChanges, setIsCancelConfirmationOpen, setEdit, onClose]);

  useEffect(() => {
    if (!edit) return undefined;

    const getOverlayRoot = () => overlayRef.current?.parentElement;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleOutsideClose();
      }
    };

    const handleOutsideMouseDown = (e: MouseEvent) => {
      if (isCancelConfirmationOpen) return;
      const overlayRoot = getOverlayRoot();
      if (overlayRoot && !overlayRoot.contains(e.target as Node)) {
        handleOutsideClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleOutsideMouseDown);
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleOutsideMouseDown);
    };
  }, [edit, handleOutsideClose, isCancelConfirmationOpen]);

  const { setDoneMutation } = useCaseMutations();
  const queryClient = useQueryClient();

  const { data: returns } = useReturnsQuery({});
  const linkedReturns = React.useMemo(() => {
    if (!saleOrder || !returns) return [];
    return returns.items.filter(r => r.sale_order?.id === saleOrder.id);
  }, [saleOrder, returns]);
  const { profile } = useProfile();
  const { data: fbaSettings } = useFbaSettingsQuery();

  const { findAccessRights } = useNavigationItems();
  const accessRights = findAccessRights('outbound');
  const chatAccess = findAccessRights('chatter');
  const casesAccess = findAccessRights('cases');
  const confirmMutation = useSaleOrderConfirm();
  const cancelMutation = useSaleOrderCancel();
  const draftMutation = useSaleOrderSetToDraft();

  const handleCaseClick = (id: number) => {
    setSelectedCaseId(id);
    setCaseClickModalOpen(true);
  };

  const handleCaseConfirmClose = (confirmed: boolean) => {
    setCaseClickModalOpen(false);
    if (confirmed && selectedCaseId !== null) {
      navigate(`/cases/${selectedCaseId}`);
    }
  };

  const handleProductClick = (id: number) => {
    setSelectedProductId(id);
    setProductClickModalOpen(true);
  };

  const handleProductConfirmClose = (confirmed: boolean) => {
    setProductClickModalOpen(false);
    if (confirmed && selectedProductId !== null) {
      navigate(`/warehouse/inventory/${selectedProductId}`);
    }
  };

  const handleRowClick = (linkedReturn: any) => {
    if (linkedReturn?.id) {
      navigate(`/returns/${linkedReturn.id}`);
    }
  };

  const callAction = async (fulfillment_state: string) => {
    try {
      if (
        (fulfillment_state === Status.draft ||
          (fulfillment_state === Status.under_investigation && (saleOrder?.linked_cases?.length ?? 0) < 1)) &&
        saleOrder?.id
      ) {
        await confirmMutation.mutateAsync({ params: { path: { sale_order_id: saleOrder.id } } });
      } else if ((fulfillment_state === Status.open || fulfillment_state === Status.not_available) && saleOrder?.id) {
        await cancelMutation.mutateAsync({ params: { path: { sale_order_id: saleOrder.id } } });
      } else if (fulfillment_state === Status.cancel && saleOrder?.id) {
        await draftMutation.mutateAsync({ params: { path: { sale_order_id: saleOrder.id } } });
      }
    } catch (error: any) {
      const detail = typeof error?.detail === 'string' ? error.detail : '';
      if (detail.includes('Cannot search for a forced carrier which is not allowed')) {
        toast.error(t('errors.carrier_not_allowed'), { autoClose: 5000 });
      } else {
        toast.error(t('outbound.operation_failed'));
      }
    }
  };

  const handleLinkedCaseClosed = async (caseId: number) => {
    await setDoneMutation.mutateAsync({
      params: {
        path: {
          model: 'project.task',
        },
      },
      body: [caseId],
    });
    if (saleOrder?.id) {
      queryClient.invalidateQueries({
        queryKey: ['get', '/sale_orders/{sale_order_id}', { params: { path: { sale_order_id: saleOrder.id } } }],
      });
    }
  };

  // Lots are optional on sale orders (unlike inbound): orders imported without
  // a lot must stay editable, so the save button only reflects the actual edit.
  // Save-button gate: extracted to validateOutboundEntry.ts (pure, unit-tested there)
  // so it can be exercised without rendering this component's full dependency tree.
  const isValid = React.useMemo(
    () => validateOutboundEntry(saleOrderItem, saleOrder as SaleOrderReadExt | undefined),
    [
      saleOrderItem?.name,
      saleOrderItem?.partner,
      saleOrderItem?.carrier_id,
      saleOrderItem?.warehouse_id,
      saleOrderItem?.order_lines,
      saleOrderItem?.is_self_collection,
      saleOrderItem?.address_confirmed,
      saleOrderItem?.incoterm_id,
      saleOrderItem?.is_cod,
      saleOrderItem?.cod_amount,
      saleOrderItem?.cod_currency_id,
      saleOrderItem?.order_note,
    ],
  );

  // A WMS-held order reads under_investigation without a case; the API
  // answers FORBIDDEN_STATE on confirm/draft (spec 2026-10-07 §9.5), so the
  // primary action is disabled with a hint instead of a generic error toast.
  const isWmsHeld = Boolean((saleOrder as SaleOrder | undefined)?.wms_hold);
  let primaryActionHoverText = '';
  if (isWmsHeld) {
    primaryActionHoverText = t('outbound.confirm_button_wms_hold');
  } else if (saleOrder?.fulfillment_state?.key === Status.under_investigation) {
    primaryActionHoverText = t('outbound.confirm_button_info');
  }

  // Orders under investigation stay locked for editing — except for the
  // "address confirmed" checkbox: an open address case puts the order exactly
  // into this state, so edit mode is offered there for that checkbox alone and
  // every other section stays read-only. The checkbox also stays visible once
  // confirmed (audit), so only an unconfirmed address opens this mode.
  const isUnderInvestigation = saleOrder?.fulfillment_state?.key === Status.under_investigation;
  const allowAddressConfirmEdit =
    isUnderInvestigation &&
    Boolean((saleOrder as SaleOrder | undefined)?.show_address_confirm) &&
    !(saleOrder as SaleOrder | undefined)?.address_confirmed &&
    !isWmsHeld;
  const editFields = edit && !isUnderInvestigation;

  return (
    <div ref={overlayRef} className="flex flex-col flex-1 min-h-0">
      {edit && (
        <div data-overlay-handles-outside="true" data-overlay-handles-escape="true" style={{ display: 'none' }} />
      )}
      <Head
        showSelectProducts={showSelectProducts}
        saleOrder={saleOrder}
        edit={edit}
        setEdit={setEdit}
        handleAction={handleAction}
        setShowSelectProducts={setShowSelectProducts}
        allowEdition={accessRights?.read_write}
        allowAddressConfirmEdit={allowAddressConfirmEdit}
        isValid={isValid}
        extraActions={
          !edit &&
          saleOrder?.fulfillment_state?.key &&
          saleOrder.fulfillment_state.key in Status && (
            <Button
              onClick={() => saleOrder?.fulfillment_state?.key && callAction(saleOrder.fulfillment_state.key)}
              title={t(
                saleOrder?.fulfillment_state?.key ? `fulfillment_state_action.${saleOrder.fulfillment_state.key}` : '',
              )}
              tone={ButtonToneProps.PRIMARY_BLUE}
              disabled={
                isWmsHeld ||
                (saleOrder?.fulfillment_state?.key === Status.under_investigation &&
                  (saleOrder?.linked_cases?.length ?? 0) > 0)
              }
              hoverText={primaryActionHoverText}
            />
          )
        }
      />
      {loading && <LoadingSkeleton />}
      <ContainerBody>
        {showSelectProducts && (
          <SelectProductsFromInventory
            defaultValue={defaultProductSelection}
            onUpdateSuccess={handleProductsSelected}
          />
        )}
        {saleOrder && !showSelectProducts && (
          <>
            <div className="flex flex-col gap-y-8 mb-12">
              <div className="flex gap-x-16 items-center">
                <h2 className="text-full typo-title-36">{saleOrder.name || saleOrder.id}</h2>

                <Badge
                  label={t(`fulfillment_state.${saleOrder.fulfillment_state.key}`)}
                  colorOverride={saleOrder.fulfillment_state.color || ''}
                  type={
                    saleOrder.fulfillment_state.color ? '' : resolveBadgeTypeForStatus(saleOrder.fulfillment_state.key)
                  }
                  expanded
                  showDot
                />
              </div>
              {(saleOrder?.linked_cases?.length ?? 0) > 0 && (
                <>
                  <OverlaySectionHeadline>{t('case.open_cases')}</OverlaySectionHeadline>
                  <LinkedCaseTable
                    linkedCaseData={saleOrder?.linked_cases ?? []}
                    onCaseClick={handleCaseClick}
                    onLinkedCaseClosed={handleLinkedCaseClosed}
                    allowClose={!!casesAccess?.read_write}
                  />
                </>
              )}
            </div>
            <div className="typo-text-14 text-faded">
              {t('general.created_at')}{' '}
              {formatDateAndTime(
                String(saleOrder.create_date),
                profile.lang ? profile.lang.replace('_', '-') : 'en-GB',
              )}
            </div>
            <Divider className="my-24" />
            <PrimaryInfo
              updateSaleOrderItem={updateSaleOrderItem}
              saleOrderItem={saleOrderItem}
              edit={editFields}
              saleOrder={saleOrder}
            />
            <Divider className="my-24" />
            <AdressForm
              showMap
              headline={t('general.receiver')}
              editable={editFields}
              withTaxId={false}
              partner={saleOrder?.partner as Partner}
              onChange={p => {
                updateSaleOrderItem('partner', p);
              }}
            />
            {(saleOrder as SaleOrder).show_address_confirm && (
              <div className="flex flex-col gap-8 mt-24">
                <div className="flex items-center gap-8">
                  <Checkbox
                    checked={saleOrderItem.address_confirmed ?? (saleOrder as SaleOrder).address_confirmed ?? false}
                    disabled={!edit}
                    onChange={e => updateSaleOrderItem('address_confirmed', e.target.checked)}
                  />
                  <span className="typo-text-14--bold text-full">{t('outbound.address_confirmed_label')}</span>
                </div>
                <span className="typo-text-12 text-faded">{t('outbound.address_confirmed_hint')}</span>
              </div>
            )}
            <Divider className="my-24" />
            <OverlaySectionHeadline>{t('general.products')}</OverlaySectionHeadline>
            <ProductTable
              key={`${saleOrder.id}-${edit}`}
              editable={editFields}
              orderLineData={(saleOrderItem.order_lines || saleOrder.order_lines) as SaleOrderLine[]}
              onAddProduct={() => setShowSelectProducts(true)}
              onChange={e => {
                updateSaleOrderItem('order_lines', e);
              }}
              onProductClick={handleProductClick}
              onOrderLineDelete={handleOrderLineDelete}
            />
            {linkedReturns.length > 0 && (
              <>
                <Divider />
                <div>
                  <OverlaySectionHeadline>{t('returns.returned_order')}</OverlaySectionHeadline>
                  <div className={classNames(styles.root, 'mb-16')}>
                    <table className="table-auto w-full border-collapse border-spacing-0 surface-elevated">
                      <thead>
                        <tr>
                          <th className="typo-text-12 text-faded text-left py-2 px-4">{t('returns.name')}</th>
                          <th className="typo-text-12 text-faded text-left py-2 px-4">{t('general.create_date')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {linkedReturns.map(linkedReturn => (
                          <tr
                            key={linkedReturn.id}
                            className={`hover:surface-elevated-more cursor-pointer ${styles.row}`}
                            onClick={() => handleRowClick(linkedReturn)}>
                            <td className="py-2 px-4">
                              <div className="flex items-center gap-x-8 md:gap-x-12">
                                <div>
                                  <h4 className="typo-text-14--bold text-full">{linkedReturn.name}</h4>
                                </div>
                              </div>
                            </td>
                            <td className="py-2 px-4">
                              <h4 className="typo-text-14--bold text-full">
                                {linkedReturn.date ? new Date(linkedReturn.date).toLocaleDateString() : '-'}
                              </h4>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}

            <Divider className="my-24" />
            <ShippingLines saleOrder={saleOrder} />
            <OverlaySectionHeadline>{t('general.additional_information')}</OverlaySectionHeadline>
            <div className="flex gap-y-16 flex-col mb-24">
              <div className="flex items-center gap-[16px]">
                <span className="typo-text-14--bold text-faded">{t('general.to_note')}</span>
                {/* controlled like the PrimaryInfo/AdressForm fields: an uncontrolled
                    defaultValue textarea mounts before the post-save refetch lands and
                    would keep showing the previous note until a full reload */}
                {!editFields && <Textarea value={saleOrder?.order_note || ''} disabled />}
                {editFields && (
                  <Textarea
                    value={saleOrderItem.order_note ?? saleOrder?.order_note ?? ''}
                    onChange={e => {
                      updateSaleOrderItem('order_note', e.target.value);
                    }}
                  />
                )}
              </div>
            </div>
            <Attachments
              attachments={saleOrder?.attachments as any[]}
              edit={editFields}
              entity={saleOrder}
              updateAttachments={files => updateSaleOrderItem('attachments', files)}
            />
            {/* FBA label upload lives only here (detail/edit overlay): the endpoint needs a
                sale_order_id, which doesn't exist yet in the create form. Gated by the
                company switch fba_label_enabled (backend freigabe, only new-WMS customers);
                no fulfillment-state gating — every existing order can receive a label. */}
            {fbaSettings?.enabled && (
              <>
                <Divider className="my-24" />
                <FbaLabel
                  saleOrderId={Number(saleOrder.id)}
                  current={(saleOrder as SaleOrderReadExt)?.fba_label ?? null}
                />
              </>
            )}
            {(chatAccess?.read || chatAccess?.read_write) && (
              <>
                <Divider className="my-24" />
                <Chat
                  model="sale.order"
                  res_id={Number(saleOrder.id)}
                  withCreateEditMessage={chatAccess?.read_write}
                  actionOptions={
                    CLAIM_ENTRY_TESTING_SHOW_ALL || saleOrder?.fulfillment_state?.key === 'shipped'
                      ? [
                          {
                            label: t('claims.new_clarification', { defaultValue: 'New clarification' }),
                            value: 'claim:clarification',
                          },
                          {
                            label: t('claims.new_damage', { defaultValue: 'New damage claim' }),
                            value: 'claim:damage',
                          },
                        ]
                      : undefined
                  }
                  onActionOption={value =>
                    navigate(
                      `/cases/claims/create?order=${saleOrder.id}&orderName=${encodeURIComponent(
                        saleOrder.name ?? '',
                      )}&type=${value.split(':')[1]}`,
                    )
                  }
                />
              </>
            )}
          </>
        )}
      </ContainerBody>
      <ConfirmationModal
        isOpen={isCancelConfirmationOpen}
        title={t('outbound.exit_edit_mode')}
        description={t('outbound.exit_edit_mode_text')}
        onClose={(confirmed: boolean) => {
          if (confirmed && pendingNavRef.current) {
            setEdit(false);
            pendingNavRef.current();
            pendingNavRef.current = null;
            setIsCancelConfirmationOpen(false);
          } else {
            const shouldClose = closeAfterConfirmRef.current;
            pendingNavRef.current = null;
            closeAfterConfirmRef.current = false;
            handleConfirmClose(confirmed);
            if (confirmed && shouldClose) {
              onClose();
            }
          }
        }}
      />
      <ConfirmationModal
        isOpen={isProductClickModalOpen}
        title={t('outbound.go_to_product')}
        description={t('outbound.go_to_product_text')}
        onClose={handleProductConfirmClose}
      />
      <ConfirmationModal
        isOpen={isCaseClickModalOpen}
        title={t('outbound.go_to_case')}
        description={t('outbound.go_to_case_text')}
        onClose={handleCaseConfirmClose}
      />
    </div>
  );
};

export default OutboundOverlay;
